/**
 * 코치 도크 — 우측 코치 패널의 접기/펼치기 껍데기(§14).
 *
 * 접으면 오른쪽 가장자리에 **얇은 손잡이**만 남고 피드가 그 폭을 가져간다.
 * 코치는 부가 정보이고 자막이 본론이므로, 필요 없을 때 폭을 돌려주는 것이
 * 옳다. 다만 접힌 동안 새 항목이 오면 **손잡이가 그 사실을 말한다** —
 * 알리지 않으면 접어 둔 사용자는 코치가 죽은 줄 안다.
 *
 * 좁은 화면에서는 피드를 밀지 않고 **덮는다**(overlay): 좁은 폭에서 둘로
 * 나누면 양쪽 다 못 읽는다.
 *
 * **코치가 꺼진 방에는 손잡이조차 없다.** 눌러도 아무것도 없는 손잡이를
 * 남기면 "여기 뭔가 있는데 안 열린다" 로 읽힌다(§14.2 채팅 버튼과 같은
 * 규율 — 서버가 끈 기능의 버튼은 그리지 않는다).
 */

import { useEffect, useRef, useState } from 'react'
import { Button } from '@cupia/ui/button'
import { PanelRightClose, PanelRightOpen } from 'lucide-react'

export interface CoachDockProps {
  /** 접힘 여부 */
  collapsed: boolean
  onToggle: () => void
  /** 좁은 화면 — 피드를 밀지 않고 덮는다 */
  narrow: boolean
  /** 접힌 동안 새 항목이 왔는지 판정할 기준값(항목 수) */
  itemCount: number
  /**
   * 패널 제목. 기본은 "AI 코치" — DEF-071 이후 호스트 화면은 "AI 어시스턴트"
   * 를 넘긴다. 기본값을 남겨 두는 이유는 되돌릴 여지다(코치를 되살리면
   * 이 껍데기가 그대로 쓰인다).
   */
  title?: string
  children: React.ReactNode
}

export function CoachDock({
  collapsed,
  onToggle,
  narrow,
  itemCount,
  title = 'AI 코치',
  children,
}: CoachDockProps): React.ReactElement {
  /** 마지막으로 **펼친 상태에서 본** 항목 수. */
  const seenRef = useRef(itemCount)
  const [unseen, setUnseen] = useState(0)

  useEffect(() => {
    if (!collapsed) {
      // 펼쳐져 있으면 보고 있는 것이다.
      seenRef.current = itemCount
      setUnseen(0)
      return
    }
    setUnseen(Math.max(0, itemCount - seenRef.current))
  }, [collapsed, itemCount])

  if (collapsed) {
    return (
      <aside
        // 손잡이는 태블릿 폭(sm≥640px)부터 보인다.
        //
        // 위로는: lg 이상에서만 그렸더니 좁은 화면에서 패널을 열 방법이 아예
        // 없었다(기본이 접힘이므로 코치가 통째로 사라진 것과 같다).
        // 아래로는: 휴대폰(<640px)에서 이 40px 이 자막 폭을 먹는다. 코치는
        // 호스트가 회의를 **진행하며** 곁눈질하는 정보라 휴대폰 화면의
        // 용례가 아니다 — 그 폭은 자막에 준다(§14 자막이 본론).
        className="hidden w-10 shrink-0 flex-col items-center border-s border-[var(--color-border)] bg-[var(--color-background)] py-3 sm:flex"
        data-testid="coach-dock-collapsed"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          aria-label={
            unseen > 0 ? `${title} 펼치기 (새 항목 ${unseen}개)` : `${title} 펼치기`
          }
          aria-expanded={false}
          title={unseen > 0 ? `${title} — 새 항목 ${unseen}개` : `${title} 펼치기`}
          data-testid="coach-toggle"
          className="relative"
        >
          <PanelRightOpen className="h-4 w-4" />
          {/* 접힌 동안 쌓인 새 항목 — 접어 둔 사용자가 놓치지 않게 */}
          {unseen > 0 && (
            <span
              data-testid="coach-unseen"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--color-primary)] px-1 text-[0.625rem] font-semibold text-[var(--color-primary-foreground)]"
            >
              {unseen > 99 ? '99+' : unseen}
            </span>
          )}
        </Button>
        {/* 세로로 세운 라벨 — 손잡이가 무엇인지 말한다 */}
        <span
          className="mt-3 select-none text-(length:--text-xs-size) text-[var(--color-muted-foreground)]"
          style={{ writingMode: 'vertical-rl' }}
          aria-hidden
        >
          {title}
        </span>
      </aside>
    )
  }

  if (narrow) {
    /*
     * 좁은 화면 — 피드를 밀지 않고 **덮는다**.
     *
     * 손잡이 자리(w-10)는 그대로 두고 그 위에 패널을 띄운다. 손잡이를
     * 치워 버리면 그 40px 만큼 본문이 넓어지며 자막이 재배치된다 —
     * "덮는다" 고 해 놓고 레이아웃을 흔드는 셈이다(실측에서 860→900px
     * 로 잡혔다).
     *
     * fixed 를 쓰는 이유: AppShell 의 rightPanel 슬롯은 relative 가 아닌
     * flex 행 안에 놓인다. absolute 로 두면 엉뚱한 조상을 기준 삼는다.
     */
    return (
      <>
        {/* 손잡이 자리를 그대로 남긴다 — 치우면 그 40px 만큼 본문이
            넓어지며 자막이 재배치된다("덮는다" 면서 레이아웃을 흔드는 셈).
            손잡이와 **같은 폭에서만** 보여야 폭 계산이 어긋나지 않는다. */}
        <aside
          className="hidden w-10 shrink-0 flex-col items-center border-s border-[var(--color-border)] bg-[var(--color-background)] py-3 sm:flex"
          aria-hidden
        />
        {/* 바깥을 누르면 닫힌다 — 덮개는 빠져나갈 길이 있어야 한다. */}
        <button
          type="button"
          aria-label={`${title} 닫기`}
          onClick={onToggle}
          className="fixed inset-0 z-20 bg-black/30"
        />
        <aside
          data-testid="coach-dock"
          className="fixed inset-y-0 right-0 z-30 flex w-[26.25rem] max-w-[85vw] flex-col gap-3 border-s border-[var(--color-border)] bg-[var(--color-background)] p-4 shadow-xl"
        >
          <div className="flex items-center gap-2">
            <h2 className="text-(length:--text-sm-size) font-semibold">{title}</h2>
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              aria-label={`${title} 접기`}
              aria-expanded
              title={`${title} 접기`}
              data-testid="coach-toggle"
              className="ms-auto"
            >
              <PanelRightClose className="h-4 w-4" />
            </Button>
          </div>
          {children}
        </aside>
      </>
    )
  }

  return (
    <aside
      data-testid="coach-dock"
      className="hidden w-[26.25rem] shrink-0 flex-col gap-3 border-s border-[var(--color-border)] bg-[var(--color-background)] p-4 lg:flex"
    >
      <div className="flex items-center gap-2">
        <h2 className="text-(length:--text-sm-size) font-semibold">{title}</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={onToggle}
          aria-label={`${title} 접기`}
          aria-expanded
          title={`${title} 접기`}
          data-testid="coach-toggle"
          className="ms-auto"
        >
          <PanelRightClose className="h-4 w-4" />
        </Button>
      </div>
      {children}
    </aside>
  )
}
